import React from "react";
import "./layouts-styles.css";

/**
 * The app's placeholder shimmer.
 *
 * A screen that is waiting on a request should show the shape of what is
 * coming, not a blank panel and not a half-built form whose buttons are
 * already spinning. Everything here is inert: no button, no field, no
 * control a person could click while there is nothing behind it.
 *
 * Variants:
 *   bar       one block — pass width/height for a heading, a chip, a button
 *   lines     `rows` stacked lines, the last one short like real text
 *   form      label/field pairs, the shape of a drawer or settings form
 *   sections  `rows` titled cards, the shape of a sectioned page
 */

const Bar = ({ w = '100%', h = 12, r, style }) => (
  <span className="skel" style={ { width: w, height: h, borderRadius: r, ...style } } />
);

const Lines = ({ rows }) => (
  <div className="skel-lines">
    { Array.from({ length: rows }).map((_, i) => (
      <Bar key={ i } w={ i === rows - 1 ? '62%' : '100%' } />
    )) }
  </div>
);

const Fields = ({ rows }) => (
  <div className="skel-fields">
    { Array.from({ length: rows }).map((_, i) => (
      <div className="skel-field" key={ i }>
        <Bar w="88px" h={ 11 } />
        <Bar h={ 38 } r="var(--r-md, 8px)" />
      </div>
    )) }
  </div>
);

const BaseSkeleton = (props) => {
  const {
    variant = 'lines',
    rows = 3,
    width,
    height,
    actions = false,
    className = '',
    style,
  } = props;

  if (variant === 'bar') {
    return <Bar w={ width } h={ height } style={ style } />;
  }

  if (variant === 'sections') {
    return (
      <div className={ `skel-sections ${className}` } style={ style }>
        { Array.from({ length: rows }).map((_, i) => (
          <div className="skel-section" key={ i }>
            <div className="skel-section-head">
              <Bar w="32px" h={ 32 } r="var(--r-md, 8px)" />
              <div className="skel-section-title">
                <Bar w="140px" h={ 14 } />
                <Bar w="220px" h={ 11 } />
              </div>
            </div>
            <Fields rows={ 2 } />
          </div>
        )) }
        { actions && <div className="skel-actions"><Bar w="120px" h={ 38 } r="var(--r-md, 8px)" /></div> }
      </div>
    );
  }

  if (variant === 'form') {
    return (
      <div className={ `skel-form ${className}` } style={ style }>
        <Fields rows={ rows } />
        { actions &&
          <div className="skel-actions">
            <Bar w="88px" h={ 38 } r="var(--r-md, 8px)" />
            <Bar w="120px" h={ 38 } r="var(--r-md, 8px)" />
          </div> }
      </div>
    );
  }

  return <Lines rows={ rows } />;
};

export default BaseSkeleton;
